import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { CreditCard, FileText, Loader2, AlertCircle, CheckCircle2, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentMethodPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null); // 'card' | 'invoice' | null
  const [error, setError] = useState('');

  const state = location.state || {};
  const {
    vehicleData, abonnement, franchise, monthlyPrice,
    firstRegistrationDate, mileage, plateNumber, commitmentMonths,
    customer, acceptsTerms, acceptsContract, sellerCode
  } = state;

  // Guard - redirect to start if no data
  React.useEffect(() => {
    if (!vehicleData || !customer) {
      navigate('/abonnements');
    }
  }, [vehicleData, customer, navigate]);

  if (!vehicleData || !customer) return null;

  // Convert backend errors into user-friendly French messages.
  // Hides raw technical leaks (Mongo timeouts, stack traces, etc.).
  const friendlyError = (err, fallback) => {
    const status = err.response?.status;
    const raw = err.response?.data?.detail;
    const text = typeof raw === 'string' ? raw : '';
    const isTechnical = /localhost|Timeout|Topology|ServerSelection|Connection refused|AutoReconnect|Traceback|<.*>/i.test(text);
    if (!err.response) {
      return 'Impossible de joindre le serveur. Vérifiez votre connexion internet et réessayez.';
    }
    if (status >= 500 || isTechnical) {
      return 'Service momentanément indisponible. Réessayez dans quelques instants — si le problème persiste, contactez-nous.';
    }
    if (status === 422) {
      return 'Certaines informations semblent invalides. Vérifiez vos données et réessayez.';
    }
    return text || fallback;
  };

  const buildPayload = () => ({
    vehicle_data: vehicleData,
    abonnement,
    franchise_chf: franchise,
    monthly_price: monthlyPrice,
    first_registration_date: firstRegistrationDate,
    mileage,
    plate_number: plateNumber,
    commitment_months: commitmentMonths,
    customer,
    accepts_terms: acceptsTerms,
    accepts_contract: acceptsContract,
    seller_code: sellerCode || null,
  });

  const handleStripeCard = async () => {
    setError('');
    setLoading('card');
    try {
      const { data } = await axios.post(`${API}/subscription/create`, buildPayload());
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('URL de paiement non reçue');
      }
    } catch (err) {
      setError(friendlyError(err, 'Erreur lors de la création du paiement par carte.'));
      setLoading(null);
    }
  };

  const handleInvoice = async () => {
    setError('');
    setLoading('invoice');
    try {
      await axios.post(`${API}/subscription/create-with-invoice`, buildPayload());
      navigate('/subscription/invoice-success', { state: { customerEmail: customer.email } });
    } catch (err) {
      setError(friendlyError(err, 'Erreur lors de l\'enregistrement de la demande.'));
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#e8f5ef] via-[#f0f9f4] to-white" data-testid="payment-method-page">
      <section className="pt-24 pb-10">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
            Choisissez votre méthode de paiement
          </h1>
          <p className="text-base text-gray-600">
            Récapitulatif : Abonnement <strong>{abonnement}</strong> · CHF {monthlyPrice?.toFixed(2)}/mois · Engagement {commitmentMonths} mois
          </p>
        </div>
      </section>

      <div className="container max-w-5xl mx-auto px-4 pb-16">
        {error && (
          <Alert variant="destructive" className="mb-6" data-testid="payment-method-error">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* Card option */}
          <Card className="border-2 hover:border-[#52a675] transition-colors" data-testid="payment-option-card">
            <CardContent className="p-8 flex flex-col h-full">
              <div className="w-14 h-14 rounded-full bg-[#52a675]/10 flex items-center justify-center mb-4">
                <CreditCard className="w-7 h-7 text-[#52a675]" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Carte bancaire</h2>
              <p className="text-gray-600 text-sm mb-6">
                Paiement immédiat et sécurisé via Stripe. Votre abonnement est activé instantanément.
              </p>
              <ul className="space-y-2 mb-6 text-sm text-gray-700">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#52a675] mt-0.5 flex-shrink-0" /> Activation immédiate</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#52a675] mt-0.5 flex-shrink-0" /> Prélèvement automatique mensuel</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#52a675] mt-0.5 flex-shrink-0" /> Visa, Mastercard, etc.</li>
              </ul>
              <Button
                onClick={handleStripeCard}
                disabled={loading !== null}
                className="mt-auto bg-[#52a675] hover:bg-[#3d8a5c] text-white py-6 text-base"
                data-testid="payment-card-submit-button"
              >
                {loading === 'card' ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Redirection...</>
                ) : (
                  <>Payer par carte <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Invoice option */}
          <Card className="border-2 hover:border-[#f59e0b] transition-colors" data-testid="payment-option-invoice">
            <CardContent className="p-8 flex flex-col h-full">
              <div className="w-14 h-14 rounded-full bg-[#f59e0b]/10 flex items-center justify-center mb-4">
                <FileText className="w-7 h-7 text-[#f59e0b]" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Facture QR</h2>
              <p className="text-gray-600 text-sm mb-6">
                Vous recevrez une facture QR par email <strong>dans les 24h ouvrables</strong>. Votre abonnement sera activé dès réception du paiement.
              </p>
              <ul className="space-y-2 mb-6 text-sm text-gray-700">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#f59e0b] mt-0.5 flex-shrink-0" /> Pas besoin de carte bancaire</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#f59e0b] mt-0.5 flex-shrink-0" /> Facture officielle PDF avec QR-IBAN</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#f59e0b] mt-0.5 flex-shrink-0" /> Première facture payable dès réception</li>
              </ul>
              <Button
                onClick={handleInvoice}
                disabled={loading !== null}
                className="mt-auto bg-[#f59e0b] hover:bg-[#d97706] text-white py-6 text-base"
                data-testid="payment-invoice-submit-button"
              >
                {loading === 'invoice' ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Enregistrement...</>
                ) : (
                  <>Recevoir une facture QR <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        <div className="text-center mt-8">
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate(-1)}
            disabled={loading !== null}
            data-testid="payment-method-back-button"
          >
            ← Retour au récapitulatif
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodPage;
